import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { QUEUE_INCOMING } from '@chama/utils/constants/status';
import clockFormat from '@chama/utils/functions/clock-format';
import getCurrentTime from '@chama/utils/functions/get-current-time';
import orderTimeStatus from '@chama/utils/functions/order-time-status';
import orderTimeCount from '@chama/utils/functions/order-time-count';

const propTypes = {
  offset: PropTypes.number.isRequired,
  iconImgSrc: PropTypes.string.isRequired,
  attentionImgSrc: PropTypes.string.isRequired,
  creationTime: PropTypes.string.isRequired,
  ticketQueue: PropTypes.string.isRequired,
  isConsumerCanceled: PropTypes.bool.isRequired,
  eta: PropTypes.string,
  isCountdown: PropTypes.bool,
};

const defaultProps = {
  isCountdown: false,
  eta: '',
};

class OrderClockTimer extends PureComponent {
  constructor(props) {
    super(props);

    const currentTime = getCurrentTime(props.offset);

    this.state = { currentTime };
  }

  componentDidMount() {
    const tickTimer = () => {
      const { offset, creationTime, eta, isCountdown } = this.props;
      const now = new Date().getTime();
      const timeUntilNextSec = 1000 - (now % 1000);

      const bumpTime = () => {
        const newCurrentTime = getCurrentTime(offset);
        const time = isCountdown ? eta : creationTime;
        const secs = orderTimeCount(newCurrentTime, time, isCountdown);
        const { hours, minutes, seconds } = clockFormat(Math.abs(secs), isCountdown);

        if (isCountdown) {
          // just update state if the minute part has changed or if other isn't late
          const isLate = secs < -1;
          const minutesChanged = minutes !== this.state.minutes;

          if (minutesChanged || !isLate) {
            this.setState({ currentTime: newCurrentTime, hours, minutes, seconds });
          }
        } else {
          const timeOverlapped = secs >= 100 * 60;

          if (!timeOverlapped) {
            this.setState({
              currentTime: newCurrentTime,
              hours,
              minutes,
              seconds,
            });
          }
        }

        tickTimer();
      };

      this.timer = setTimeout(bumpTime, timeUntilNextSec);
    };

    tickTimer();
  }

  componentWillUnmount() {
    if (this.timer) {
      clearTimeout(this.timer);
    }
  }

  hasAttentionNeeded = (danger, warning) => {
    const { ticketQueue, isConsumerCanceled } = this.props;

    return (danger || warning) && ticketQueue === QUEUE_INCOMING && !isConsumerCanceled;
  };

  render() {
    const { iconImgSrc, creationTime, isCountdown, eta, ticketQueue, attentionImgSrc } = this.props;
    const { currentTime } = this.state;
    const time = isCountdown ? eta : creationTime;
    const secs = orderTimeCount(currentTime, time, isCountdown);
    const { danger, warning } = orderTimeStatus(ticketQueue, currentTime, eta, creationTime);
    const durationClass = classNames('OrderSnippet__duration', {
      'OrderSnippet__duration--danger': danger,
      'OrderSnippet__duration--warning': warning,
      'OrderSnippet__duration--attention_needed': this.hasAttentionNeeded(danger, warning),
      'OrderSnippet__duration--is-late': secs < 0,
    });

    const { hours, minutes, seconds } = clockFormat(Math.abs(secs), isCountdown);

    const timeOverlapped = secs >= 100 * 60;
    const minutesLabel = timeOverlapped ? '99' : minutes;
    const secondsLabel = timeOverlapped ? '99' : seconds;

    if (this.hasAttentionNeeded(danger, warning)) {
      return (
        <span className={durationClass}>
          <span key="attention_duration" className="OrderSnippet__attention_duration_content">
            <div>
              <div>
                <img className="OrderSnippet__attention_icon" src={attentionImgSrc} alt="alarm" />
              </div>
              <div className="OrderSnippet__attention_duration_text">
                {minutesLabel}:{secondsLabel}
              </div>
            </div>
          </span>
        </span>
      );
    }

    if (secs < 120 * 60 && secs >= 0) {
      return (
        <span className={durationClass}>
          <img src={iconImgSrc} alt="iconImgSrc" />
          &nbsp;
          <span className="OrderSnippet__durationText">
            {hours
              ? `${Math.abs(hours)}h ${Math.abs(minutes)}m`
              : `${minutesLabel}:${secondsLabel}`}
          </span>
        </span>
      );
    }

    if (isCountdown) {
      const isLateLessThanOneMinute = hours === 0 && Math.abs(minutes) === 0;

      return (
        <span className={durationClass}>
          <FormattedMessage id="general.order_late" />
          {!isLateLessThanOneMinute && (
            <div>
              {hours ? `${Math.abs(hours)}h ${Math.abs(minutes)}m` : `${Math.abs(minutes)}m`}
            </div>
          )}
        </span>
      );
    }

    return (
      <span className={durationClass}>
        <img src={iconImgSrc} alt="." />
        &nbsp;
        <span className="OrderSnippet__durationText">99:99</span>
      </span>
    );
  }
}

OrderClockTimer.propTypes = propTypes;
OrderClockTimer.defaultProps = defaultProps;

export default OrderClockTimer;



// WEBPACK FOOTER //
// ./src/modules/orders/components/OrderClockTimer/index.jsx